import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Facebook, Youtube, Twitter, Linkedin, Music2, Globe } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  twitter: Twitter,
  x: Twitter,
  linkedin: Linkedin,
  tiktok: Music2,
  spotify: Music2,
};

function iconFor(s) {
  const key = (s.icon || s.label || "").toString().toLowerCase();
  return ICONS[key] || Globe;
}

export default function Footer() {
  const content = useSite();
  const footer = content.footer || {};
  const socials = content.socials || [];

  return (
    <footer data-testid="main-footer" className="relative bg-black text-white grain">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 py-20 md:grid-cols-12 md:py-28">
          <div className="md:col-span-6">
            <p className="font-mono-hw text-[11px] uppercase tracking-[0.3em] text-gavra">{footer.eyebrow}</p>
            <h2 className="mt-6 font-heading text-4xl font-extrabold uppercase leading-none tracking-tight md:text-5xl">
              {footer.headline}
            </h2>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="mt-8 inline-flex items-center gap-2 border border-white/30 px-6 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra hover:border-gavra"
            >
              Get in touch <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono-hw text-[11px] uppercase tracking-[0.2em] text-white/40">Explore</p>
            <ul className="mt-5 space-y-3">
              {[
                { to: "/products", label: "Products" },
                { to: "/artists", label: "Artists" },
                { to: "/dealers", label: "Dealers" },
                { to: "/about", label: "About Us" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm font-medium text-white/80 transition-colors hover:text-gavra">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono-hw text-[11px] uppercase tracking-[0.2em] text-white/40">Contact</p>
            <ul className="mt-5 space-y-3 text-sm text-white/80">
              <li>{footer.email}</li>
              <li>{footer.web}</li>
            </ul>

            {socials.length > 0 && (
              <div className="mt-8">
                <p className="font-mono-hw text-[11px] uppercase tracking-[0.2em] text-white/40">Follow</p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {socials.map((s, i) => {
                    const Icon = iconFor(s);
                    return (
                      <a
                        key={i}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        data-testid={`social-${(s.label || s.icon || i).toString().toLowerCase()}`}
                        className="flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors hover:bg-gavra hover:border-gavra"
                      >
                        <Icon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 py-8 text-[11px] uppercase tracking-[0.15em] text-white/40 md:flex-row md:items-center md:justify-between">
          <span>© {new Date().getFullYear()} {footer.copyright}</span>
          <span>{footer.tagline}</span>
        </div>
      </div>
    </footer>
  );
}
